import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { ChannelProvider, ChannelRow } from "@/features/channels/channelManifest";
import type {
  ConnectSmsResult,
  ConnectWhatsAppResult,
  DisconnectResult,
  IChannelService,
  ReconnectEmailResult,
  TelegramLinkResult,
  VerifySmsResult,
  VerifyWhatsAppResult,
} from "@/services/interfaces/IChannelService";

const DISCONNECT_RESULTS: readonly DisconnectResult[] = ["disconnected", "not_connected", "not_disconnectable"];

const RECONNECT_RESULTS: readonly ReconnectEmailResult[] = [
  "reconnected",
  "already_connected",
  "admin_revoked",
  "no_address",
];

export class SupabaseChannelService implements IChannelService {
  // RLS filters this to the calling user's own contacts via uc_select_own
  // (user_id = requesting_user_id()). We additionally filter `revoked_at IS
  // NULL` so revoked rows are treated as "not connected" on the client.
  async listMine(): Promise<ChannelRow[]> {
    const { data, error } = await supabase
      .from("user_contacts")
      .select("provider, contact, opted_in, revoked_at")
      .is("revoked_at", null);
    if (error) throw new Error(error.message);
    return (data ?? []) as ChannelRow[];
  }

  // opted_in is the one column (with `preferences`) that `authenticated` may
  // UPDATE (20260526120000).
  async setOptIn(provider: ChannelProvider, optedIn: boolean): Promise<void> {
    const { data, error } = await supabase
      .from("user_contacts")
      .update({ opted_in: optedIn })
      .eq("provider", provider)
      .is("revoked_at", null)
      .select("provider");
    if (error) throw new Error(error.message);
    // RLS turns a write it refuses into zero rows, not an error. Count them,
    // so a refused write shows up as a failure and the switch snaps back
    // rather than showing a state the server never stored.
    if (!data || data.length === 0) {
      throw new Error(`No active ${provider} contact to update`);
    }
  }

  async disconnect(provider: ChannelProvider): Promise<DisconnectResult> {
    const { data, error } = await supabase.rpc("disconnect_channel", {
      p_provider: provider,
    });
    if (error) throw new Error(error.message);
    // The RPC only ever returns these literals; narrow defensively so a
    // future schema change surfaces here instead of silently flowing on.
    if (!DISCONNECT_RESULTS.includes(data as DisconnectResult)) {
      throw new Error(`Unexpected disconnect_channel result: ${String(data)}`);
    }
    return data as DisconnectResult;
  }

  async reconnectEmail(): Promise<ReconnectEmailResult> {
    const { data, error } = await supabase.rpc("reconnect_email_contact");
    if (error) throw error;
    // Narrow rather than cast, as disconnect does: handleConnect routes
    // anything it does not recognise to the "no email address" message, so an
    // unmodelled outcome would render a confidently wrong explanation instead
    // of surfacing an error.
    if (!RECONNECT_RESULTS.includes(data as ReconnectEmailResult)) {
      throw new Error(`Unexpected reconnect_email_contact result: ${String(data)}`);
    }
    return data as ReconnectEmailResult;
  }

  startSmsLink(phone: string): Promise<ConnectSmsResult> {
    return callEdgeFunction<ConnectSmsResult>("sms-link-start", { body: { phone } });
  }

  verifySms(tokenId: string, code: string): Promise<VerifySmsResult> {
    return callEdgeFunction<VerifySmsResult>("sms-link-verify", { body: { tokenId, code } });
  }

  startWhatsAppLink(phone: string): Promise<ConnectWhatsAppResult> {
    return callEdgeFunction<ConnectWhatsAppResult>("whatsapp-link-start", { body: { phone } });
  }

  verifyWhatsApp(tokenId: string, code: string): Promise<VerifyWhatsAppResult> {
    return callEdgeFunction<VerifyWhatsAppResult>("whatsapp-link-verify", { body: { tokenId, code } });
  }

  startTelegramLink(): Promise<TelegramLinkResult> {
    return callEdgeFunction<TelegramLinkResult>("telegram-link-start", { body: {} });
  }
}
